import {
  type MutateOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { gql } from 'graphql-request';
import { useCollection } from '@/features/events/contexts/CollectionContext/useCollection';
import { useEffectiveExperience } from '@/features/experiences/contexts/PageExperienceContext/useEffectiveExperience';
import { gqlClient } from '@/lib';
import type { QueryError } from '@/utils/errors';
import { createExperienceQueryKey } from '@/utils/queryKeys';
import type {
  UnsubscribeExperienceFromCalendarMutation,
  UnsubscribeExperienceFromCalendarMutationVariables,
} from './useUnsubscribeExperienceFromCalendar.generated';

const query = gql`
  mutation UnsubscribeExperienceFromCalendar(
    $input: UnsubscribeExperienceFromCalendarInput!
  ) {
    unsubscribeExperienceFromCalendar(input: $input)
  }
`;

// Stops the ongoing sync; with removeEvents the events the subscription pushed
// are also deleted from the calendar. Either way the server mutates export
// rows synchronously, so the events refetch alongside the experience.
export const useUnsubscribeExperienceFromCalendar = () => {
  const queryClient = useQueryClient();
  const experience = useEffectiveExperience();
  const { revalidate } = useCollection();

  const { mutate, ...rest } = useMutation<
    UnsubscribeExperienceFromCalendarMutation,
    QueryError,
    UnsubscribeExperienceFromCalendarMutationVariables
  >({
    mutationFn: (variables) => gqlClient.request(query, variables),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: createExperienceQueryKey(experience.id),
        exact: true,
      });
      if (experience.publishedSlug) {
        queryClient.invalidateQueries({
          queryKey: createExperienceQueryKey(experience.publishedSlug),
          exact: true,
        });
      }
      revalidate();
    },
  });

  const unsubscribeFromCalendar = (
    subscriptionId: string,
    removeEvents: boolean,
    options?: MutateOptions<
      UnsubscribeExperienceFromCalendarMutation,
      QueryError,
      UnsubscribeExperienceFromCalendarMutationVariables
    >
  ) => mutate({ input: { subscriptionId, removeEvents } }, options);

  return { unsubscribeFromCalendar, ...rest };
};
